import { clsx } from 'clsx'
import { KeyboardEvent, forwardRef, useCallback, useRef, useState } from 'react'
import { useUiKit } from '../providers'
import styles from './Input.module.scss'
import { Text } from './Text'

interface InputProps {
  placeholder?: string
  label?: string
  value?: string
  onChange?: (value: string) => void
  onBlur?: () => void
  onKeyDown?: (event: KeyboardEvent) => void
  onKeyEnter?: (value: string) => void
  isDisabled?: boolean
  defaultPristine?: boolean
  isError?: boolean
  errorText?: string
  className?: string
  inputClassName?: string
  variant?: 'default' | 'inline'
  rounded?: 'full' | 'start' | 'end'
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      placeholder,
      label,
      value,
      onChange,
      onBlur,
      onKeyDown,
      onKeyEnter,
      isDisabled,
      isError,
      errorText,
      className,
      inputClassName,
      variant = 'default',
      defaultPristine = true,
      rounded = 'full'
    },
    ref
  ): React.JSX.Element => {
    const defaultRef = useRef<HTMLInputElement>(null)
    const [isPristine, setIsPristine] = useState<boolean>(defaultPristine)
    const [isFocused, setIsFocused] = useState(false)
    const { theme } = useUiKit()

    const onInputBlur = useCallback(() => {
      setIsFocused(false)
      onBlur?.()
      setIsPristine(false)
    }, [onBlur])

    const onKeyDownWrapper = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
          onKeyEnter?.((event.target as HTMLInputElement)?.value || '')
        }

        onKeyDown?.(event)
      },
      [onKeyDown, onKeyEnter]
    )

    return (
      <div className={clsx('relative flex flex-col', className)}>
        {label && (
          <Text className="text-gray-90 absolute -top-5 right-0" type="body3-regular">
            {label}
          </Text>
        )}
        <div
          className={clsx(styles.Input, inputClassName, 'flex-1 transition-all', {
            'rounded-xl border border-solid': variant === 'default',
            'border-b border-solid': variant === 'inline',
            'rounded-e-none': rounded === 'start',
            'rounded-s-none': rounded === 'end',
            [styles.isFocused]: isFocused,
            [styles.isDisabled]: isDisabled,
            [styles.isError]: !isPristine && isError
          })}
        >
          <input
            ref={ref || defaultRef}
            className={clsx('w-full font-medium overflow-ellipsis outline-0', {
              'backgroundColorBlackToWhite rounded-xl p-4': variant === 'default',
              'rounded-e-none': rounded === 'start',
              'rounded-s-none': rounded === 'end',
              'bg-transparent p-1': variant === 'inline'
            })}
            style={
              variant === 'default'
                ? {
                    paddingTop: value && placeholder ? '22px' : '16px',
                    paddingBottom: value && placeholder ? '10px' : '16px'
                  }
                : {}
            }
            value={value}
            onChange={({ target }) => onChange?.(target.value)}
            onBlur={onInputBlur}
            onKeyDown={onKeyDownWrapper}
            disabled={isDisabled}
            type="text"
          />
        </div>
        <Text
          className={clsx(
            'text-gray-90 pointer-events-none absolute left-4 transition select-none',
            value ? 'top-2' : 'top-4'
          )}
          style={{ transitionProperty: 'all' }}
          type={value ? 'body3-regular' : 'body1-regular'}
        >
          {placeholder}
        </Text>
        {isError && !isPristine && (
          <Text className="colorError mt-1" type="body3-regular">
            {errorText}
          </Text>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'
